'use client';

import classnames from 'classnames';
import { useLocale, useTranslations } from 'next-intl';
import React, { useMemo, useState } from 'react';

import TicketButtonGrouped from '@/app/[locale]/(default-layout)/program/_components/TicketButtonGrouped';

import Button from '@/components/button/Button';
import { ButtonStyle } from '@/components/button/ButtonStyle.enum';
import CardWrapper from '@/components/CardWrapper.tsx';

import { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';

import { HrefOrHrefWithParams } from 'next-intl/dist/types/navigation/shared/utils';

import { useCachedContentData } from '@/hooks/useCachedContentData';
import { useContent } from '@/hooks/useContent';

import { IconDown16 } from '@cineamo/ui/src/icons/ic-down';
import ImageWithTransition from '@cineamo/ui/src/image/ImageWithTransition';
import Tooltip from '@cineamo/ui/src/tooltip/Tooltip';

import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';

import { Link } from '@/i18n/routing';
import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

const MAX_VISIBLE_SHOWINGS = 4;
const INITIAL_SHOWINGS_COUNT = 1;

// All showings belong to the same content (movie); they are pre-sorted by startDatetime ascending.
export default function HighlightSectionItem({ showings }: { showings: ShowingDto[] }): React.JSX.Element {
    const domainConfiguration = useDomainConfiguration();
    const { track } = useAnalytics();

    const locale = useLocale();
    const t = useTranslations();
    const cinema = domainConfiguration.cinema;

    const [isExpanded, setIsExpanded] = useState<boolean>(false);

    const firstShowing = showings[0];

    const contentDto = useContent(firstShowing.contentId);
    const { data: content } = useCachedContentData({ content: contentDto, locale, cinema });

    const visibleShowings = useMemo(
        () => (isExpanded ? showings.slice(0, MAX_VISIBLE_SHOWINGS) : showings.slice(0, INITIAL_SHOWINGS_COUNT)),
        [showings, isExpanded]
    );

    const showingName = content?.title;
    const showingHighlightText = firstShowing?.highlightText;
    const showingBackdropImageUrl = content?.backdropImage;
    const showingPosterImage = content?.posterImage;

    // Render a fixed-size placeholder (instead of null) while the content loads, so the slider has
    // its real width on mount. Otherwise the scroll-state would measure an empty (non-scrollable)
    // row and the left/right arrows would never appear once the content arrives.
    if (!content) {
        return (
            <CardWrapper
                className="relative flex-none w-[290px] lg:w-[330px] min-h-[375px] lg:min-h-[412px] rounded-8 border-2 border-[var(--surface-color)] overflow-hidden animate-pulse-skeleton"
                domainConfiguration={domainConfiguration}
            />
        );
    }

    const ticketStyle = {
        backgroundColor: domainConfiguration.style.surfaceColor,
        color: domainConfiguration.style.onSurfaceColor
    };
    const ticketTagStyle = {
        backgroundColor: domainConfiguration.style.primaryColor,
        color: domainConfiguration.style.onPrimaryColor
    };

    const showingsCount = showings.length;

    return (
        <CardWrapper
            className="relative flex-none w-[290px] lg:w-[330px] min-h-[375px] lg:min-h-[412px] rounded-8 border-2 border-[var(--surface-color)] overflow-hidden flex flex-col gap-8 group"
            domainConfiguration={domainConfiguration}>
            <Link
                className="h-[165px] lg:h-[192px] flex-none"
                href={
                    {
                        pathname: '/program/[contentId]',
                        params: {
                            contentId: firstShowing.contentId
                        }
                    } as HrefOrHrefWithParams<string>
                }
                onClick={() =>
                    track('Highlight Item Clicked', {
                        contentId: firstShowing.contentId,
                        title: showingName
                    })
                }
                /*
                 * Prefetch the full page data beyond the loading.js boundaries by setting the prefetch prop to true
                 * (See: https://nextjs.org/docs/app/building-your-application/routing/linking-and-navigating#2-prefetching)
                 */
                aria-label={showingName}
                prefetch={false}>
                <div
                    className={classnames(
                        'relative w-full h-full',
                        'gradient-mask-b-[#D9D9D9_59.5%,transparent_92.93%]'
                    )}>
                    <ImageWithTransition
                        className={classnames('w-full h-full object-cover group-hover:scale-105 duration-200')}
                        src={showingBackdropImageUrl}
                        alt={`${showingName} Backdrop`}
                        width={330}
                        height={192}
                    />
                    {/* Dark gradient overlay for mobile */}
                    <div className={classnames('absolute w-1/2 h-full left-0 top-0 bg-black/85 gradient-mask-r-60')} />
                </div>
                {/* Poster visible only for mobile */}
                <div
                    className={classnames(
                        'absolute',
                        'left-8 top-0',
                        'my-8',
                        'h-[141px] lg:h-[168px] aspect-[2/3]',
                        'rounded-3 lg:rounded-8 overflow-hidden'
                    )}>
                    <ImageWithTransition
                        className="w-full h-full object-cover"
                        src={showingPosterImage}
                        alt={`${showingName} Poster`}
                        width={150}
                        height={200}
                    />
                </div>
            </Link>
            <div className="w-full flex flex-col space-y-0 px-12 justify-between">
                <div className="w-full flex flex-col pb-16 flex-auto justify-center text-[var(--on-surface-color)]">
                    <Tooltip
                        tooltipText={showingName}
                        isDisabled={showingName.length < 25}
                        usePortal>
                        <h3 className="text-body-bold-16 whitespace-nowrap text-ellipsis truncate">{showingName}</h3>
                    </Tooltip>
                    {showingHighlightText && (
                        <span className="text-body-12 line-clamp-2 opacity-80 leading-[20px]">
                            {showingHighlightText}
                        </span>
                    )}
                </div>

                <div className="flex flex-col gap-8">
                    <div
                        className={classnames(
                            'w-full grid grid-cols-1 gap-8 h-[var(--cards-height)] overflow-hidden duration-200'
                        )}
                        style={
                            {
                                '--cards-height': `${visibleShowings.length * 120 + (visibleShowings.length - 1) * 8}px`
                            } as React.CSSProperties
                        }>
                        {visibleShowings.map((groupedShowing) => (
                            <TicketButtonGrouped
                                key={`HighlightShowing#${groupedShowing.id}`}
                                content={content}
                                className="flex-none h-[120px]"
                                showing={groupedShowing}
                                showTags
                                showDateAndTime
                                showingTagStyle={ticketTagStyle}
                                style={ticketStyle}
                            />
                        ))}
                    </div>
                </div>
            </div>

            {isExpanded && showingsCount > MAX_VISIBLE_SHOWINGS && (
                <Link
                    className={classnames(
                        'w-full flex justify-center overflow-hidden',
                        isExpanded ? 'h-[46px]' : 'h-[0px]'
                    )}
                    href={
                        {
                            pathname: '/program/[contentId]',
                            params: {
                                contentId: firstShowing.contentId
                            }
                        } as HrefOrHrefWithParams<string>
                    }
                    aria-label={showingName}
                    prefetch={false}>
                    <Button
                        style={ButtonStyle.text_only}
                        labelClassName="text-body-bold-14"
                        css={{
                            color: domainConfiguration.style.onBackgroundColor
                        }}>
                        {t('common.more')}
                    </Button>
                </Link>
            )}
            {showingsCount > INITIAL_SHOWINGS_COUNT ? (
                <button
                    className={classnames('w-full flex justify-center items-center mt-auto space-x-6 pb-12')}
                    onClick={() => {
                        track('Expanded Highlight Section', {
                            deviceWidth: window.innerWidth,
                            deviceHeight: window.innerHeight
                        });
                        setIsExpanded(!isExpanded);
                    }}>
                    <span className="text-body-12">
                        {isExpanded
                            ? t('home.show-less-showings')
                            : t('home.show-more-showings', {
                                  count: Math.min(showingsCount, MAX_VISIBLE_SHOWINGS) - INITIAL_SHOWINGS_COUNT
                              })}
                    </span>
                    <IconDown16 className={classnames('duration-150', { 'rotate-180': isExpanded })} />
                </button>
            ) : (
                <div className="h-[30px]" />
            )}
        </CardWrapper>
    );
}
